// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Supabase Auth client, narrowed to what the login bridge uses.
 *
 * Supabase owns users and passwords; ruxd only trusts its JWTs. The browser
 * signs in here, then trades the access token for ruxd's HttpOnly session
 * cookie (`POST /api/v1/auth/session`, see `app/session.ts`), which is what
 * every API call, `<img>` and the events socket actually carry.
 *
 * supabase-js is imported on demand, so local mode (no `/config.json`)
 * never downloads it.
 */

import type { SupabaseConfig } from '../config';

/** Just the part of a Supabase session the bridge needs. */
export interface SupabaseSession {
  access_token: string;
}

/** A Supabase Auth refusal: `code` is GoTrue's machine-readable reason. */
export interface SupabaseAuthFailure {
  message: string;
  status?: number;
  code?: string;
}

export type SupabaseAuthEvent =
  | 'INITIAL_SESSION'
  | 'SIGNED_IN'
  | 'SIGNED_OUT'
  | 'TOKEN_REFRESHED'
  | 'USER_UPDATED'
  | 'PASSWORD_RECOVERY'
  | (string & {});

/** The Supabase Auth calls the app makes; a mock in the tests. */
export interface SupabaseAuth {
  /** The stored session, refreshed first if its access token has expired. */
  getSession(): Promise<SupabaseSession | null>;
  /** Force a refresh; null when the refresh token is gone or revoked. */
  refreshSession(): Promise<SupabaseSession | null>;
  signInWithPassword(email: string, password: string): Promise<SupabaseSession>;
  /** Mail a one-time login link that comes back to `redirectTo`. Never signs anyone up. */
  sendLoginLink(email: string, redirectTo: string): Promise<void>;
  /** Forget the session in this browser. */
  signOut(): Promise<void>;
  onAuthStateChange(listener: (event: SupabaseAuthEvent, session: SupabaseSession | null) => void): () => void;
}

/** The signed-in account as Supabase holds it (ruxd-wmc.5). */
export interface SupabaseAccountUser {
  email: string;
  /** `user_metadata.username`; empty when never set. */
  username: string;
  /** A requested e-mail change awaiting confirmation, if any. */
  pendingEmail: string | null;
}

/** What the account changes it carries out; omitted fields stay as they are. */
export interface SupabaseAccountChange {
  email?: string;
  password?: string;
  username?: string;
}

/**
 * Self-service account calls (Konto). The same client as {@link SupabaseAuth},
 * kept as a separate interface so the session bridge's stays narrow.
 */
export interface SupabaseAccount {
  currentUser(): Promise<SupabaseAccountUser | null>;
  /** Re-prove the password by signing in again; throws `invalid_credentials` when wrong. */
  verifyPassword(email: string, password: string): Promise<void>;
  updateUser(change: SupabaseAccountChange): Promise<SupabaseAccountUser>;
}

/** Thrown for a refused Supabase call. */
export class SupabaseAuthError extends Error implements SupabaseAuthFailure {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'SupabaseAuthError';
  }
}

function fail(error: { message: string; status?: number; code?: string }): never {
  throw new SupabaseAuthError(error.message, error.status, error.code);
}

/** A real client, for the configured project. */
export async function createSupabaseAuth(config: SupabaseConfig): Promise<SupabaseAuth & SupabaseAccount> {
  const { createClient } = await import('@supabase/supabase-js');
  const { auth } = createClient(config.url, config.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // A login link comes back as `?code=…`; PKCE keeps the token out of the URL.
      detectSessionInUrl: true,
      flowType: 'pkce',
      storageKey: 'reusex-supabase-auth',
    },
  });
  return {
    async getSession() {
      const { data, error } = await auth.getSession();
      if (error) fail(error);
      return data.session;
    },
    async refreshSession() {
      const { data, error } = await auth.refreshSession();
      return error ? null : data.session;
    },
    async signInWithPassword(email, password) {
      const { data, error } = await auth.signInWithPassword({ email, password });
      if (error) fail(error);
      if (!data.session) fail({ message: 'no session in the answer' });
      return data.session;
    },
    async sendLoginLink(email, redirectTo) {
      const { error } = await auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
      });
      if (error) fail(error);
    },
    async signOut() {
      const { error } = await auth.signOut({ scope: 'local' });
      if (error) fail(error);
    },
    async currentUser() {
      const { data, error } = await auth.getUser();
      if (error) return null;
      return accountUser(data.user);
    },
    async verifyPassword(email, password) {
      const { error } = await auth.signInWithPassword({ email, password });
      if (error) fail(error);
    },
    async updateUser(change) {
      const { data, error } = await auth.updateUser({
        email: change.email,
        password: change.password,
        data: change.username === undefined ? undefined : { username: change.username },
      });
      if (error) fail(error);
      return accountUser(data.user);
    },
    onAuthStateChange(listener) {
      const { data } = auth.onAuthStateChange((event, session) => listener(event, session));
      return () => data.subscription.unsubscribe();
    },
  };
}

function accountUser(user: {
  email?: string;
  new_email?: string;
  user_metadata?: Record<string, unknown>;
}): SupabaseAccountUser {
  const username = user.user_metadata?.['username'];
  return {
    email: user.email ?? '',
    username: typeof username === 'string' ? username : '',
    pendingEmail: user.new_email ?? null,
  };
}
